import React, { useRef, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import tinyColor from 'tinycolor2';
import { Tooltip } from '@doxyme/ui';
import { ResetButton } from './reset-button';
import { DEFAULT_THEME_VALUES } from '../constants';
import './color-picker.scss';
const hexRegExp = /^#([\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/u;
const validateHex = (val) => {
    if (!val) {
        return false;
    }
    if (!val.includes('#')) {
        return false;
    }
    return hexRegExp.test(val);
};
export const ColorPicker = ({ value, onChange, onBlur, onReset, placeholder, required, showReset = false, fieldName = '', defaultValue = '', accentColor, textInputId, colorPickerId, resetButtonId, frontColor, backgroundColor, showHelpIcon = false, helpTooltipText, }) => {
    const intl = useIntl();
    const formatMessage = intl.formatMessage.bind(intl);
    const lastValidColorRef = useRef(value || defaultValue || '');
    // Calculate darkened frontColor for welcomeScreenText when empty
    const darkenedFrontColor = useMemo(() => {
        if (frontColor && validateHex(frontColor)) {
            return tinyColor(frontColor).darken(5).toString();
        }
        return null;
    }, [frontColor]);
    // Calculate contrast color for contrastBack when empty (pickContrast logic)
    const contrastColor = useMemo(() => {
        if (backgroundColor && validateHex(backgroundColor)) {
            const rgb = {
                r: Number.parseInt(backgroundColor.slice(1, 3), 16),
                g: Number.parseInt(backgroundColor.slice(3, 5), 16),
                b: Number.parseInt(backgroundColor.slice(5, 7), 16),
            };
            // Same formula as ColorService.pickContrast
            const brightness = rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114;
            return brightness > 186 ? '#000000' : '#ffffff';
        }
        return null;
    }, [backgroundColor]);
    // Update last valid color when value is valid
    useEffect(() => {
        if (value && validateHex(value)) {
            lastValidColorRef.current = value;
        }
    }, [value]);
    const handleChange = (e) => {
        onChange(e.target.value);
    };
    const handleBlur = () => {
        // If field is empty, revert to last valid color or default
        // Exceptions: welcomeScreenText and contrastBack are allowed to be empty (have fallback behavior)
        if (!value || value.trim() === '') {
            // Allow empty for welcomeScreenText and contrastBack (they have special fallback behavior)
            if (fieldName === 'welcomeScreenText' || fieldName === 'contrastBack') {
                if (onBlur) {
                    onBlur(fieldName);
                }
                return;
            }
            // For all other fields, revert to last valid value or default
            const revertValue = lastValidColorRef.current ||
                defaultValue ||
                (fieldName
                    ? DEFAULT_THEME_VALUES[fieldName]
                    : '') ||
                '';
            if (revertValue) {
                onChange(revertValue);
                // Delay onBlur call to allow Formik to re-validate after value revert
                // This ensures validation errors are cleared when value is reverted
                setTimeout(() => {
                    if (onBlur) {
                        onBlur(fieldName);
                    }
                }, 0);
                return;
            }
        }
        // If invalid hex, don't revert - let validation message show and disable Save button
        if (onBlur) {
            onBlur(fieldName);
        }
    };
    // Determine color picker value: use calculated fallback colors when value is empty
    const colorPickerValue = useMemo(() => {
        if (value && validateHex(value)) {
            return value;
        }
        // For welcomeScreenText: if empty, show darkened frontColor
        if (fieldName === 'welcomeScreenText' && !value && darkenedFrontColor) {
            return darkenedFrontColor;
        }
        // For contrastBack: if empty, show contrast color based on backgroundColor
        if (fieldName === 'contrastBack' && !value && contrastColor) {
            return contrastColor;
        }
        return lastValidColorRef.current || defaultValue || '#000000';
    }, [value, darkenedFrontColor, contrastColor, defaultValue, fieldName]);
    return (<div className="color-picker-wrapper">
      <div className="color-picker-input-wrapper">
        <input id={textInputId} type="text" className="form-control" value={value} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder ??
            formatMessage({
                id: 'account.settings.clinic.branding.color-placeholder',
            })} maxLength={7} required={required}/>
        {showHelpIcon && helpTooltipText && (<Tooltip className="color-picker-help-tooltip" tabIndex={-1} content={helpTooltipText} size="small" theme="dark" pointerPosition="top-center" hideTimeout={0} debounceMs={0}>
            <span className="color-picker-help-icon" aria-label={helpTooltipText}>
              <i className="icon icon-help-circled"></i>
            </span>
          </Tooltip>)}
      </div>
      <input id={colorPickerId} type="color" className="color-picker-input" value={colorPickerValue} onChange={handleChange}/>
      {showReset && onReset && (<ResetButton onClick={onReset} accentColor={accentColor} resetButtonId={resetButtonId}/>)}
    </div>);
};
